import { useRef, useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { Popover, PopoverTrigger } from '../lib/shadcn/popover'
import { Button } from '../lib/shadcn/button'
import { cn } from '../lib/shadcn/utils'

// A button that opens a month calendar. Value is a 'YYYY-MM-DD' string ('' when empty),
// matching what <input type="date"> used to give.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function parse(value: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '')
  if (!match) return null
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) }
}

function pad(n: number) {
  return String(n).padStart(2, '0')
}

function toValue(y: number, m: number, d: number) {
  return `${y}-${pad(m + 1)}-${pad(d)}`
}

function todayParts() {
  const now = new Date()
  return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() }
}

export function formatDateLabel(value: string): string {
  const p = parse(value)
  if (!p) return ''
  return `${MONTHS[p.m]!.slice(0, 3)} ${p.d}, ${p.y}`
}

interface DatePickerProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  // Month to show when nothing is picked yet, as 'YYYY-MM-DD' (e.g. the start date).
  defaultMonth?: string
  disabled?: boolean
  className?: string
}

export default function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'Pick a date',
  defaultMonth,
  disabled = false,
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState(() => todayParts())
  // Inside a dialog, render the calendar in the dialog so its focus trap
  // doesn't treat the calendar as outside (same as IngredientCombobox, issue #40).
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [container, setContainer] = useState<HTMLElement | null>(null)

  const selected = parse(value)
  const today = todayParts()

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null)
      const start = selected ?? parse(defaultMonth ?? '') ?? today
      setView({ y: start.y, m: start.m, d: 1 })
    }
    setOpen(next)
  }

  const shiftMonth = (delta: number) =>
    setView(v => {
      const m = v.m + delta
      return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12, d: 1 }
    })

  const firstWeekday = new Date(view.y, view.m, 1).getDay()
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate()
  const cells: (number | null)[] = [
    ...Array<null>(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]

  const pick = (d: number) => {
    onChange(toValue(view.y, view.m, d))
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn('w-full justify-start font-normal', className)}
        >
          <CalendarDays className="mr-2 h-4 w-4 shrink-0 opacity-60" />
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? formatDateLabel(value) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverPrimitive.Portal container={container}>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionBoundary={container ? [container] : []}
          collisionPadding={8}
          className="z-50 w-72 rounded-md border bg-popover p-3 text-popover-foreground shadow-retool-md outline-none pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <div className="mb-2 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium">
              {MONTHS[view.m]} {view.y}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEKDAYS.map(w => (
              <div key={w} className="py-1 text-xs text-muted-foreground">
                {w}
              </div>
            ))}
            {cells.map((d, i) => {
              if (d === null) return <div key={`blank-${i}`} />
              const isSelected =
                !!selected && selected.y === view.y && selected.m === view.m && selected.d === d
              const isToday = today.y === view.y && today.m === view.m && today.d === d
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => pick(d)}
                  aria-pressed={isSelected}
                  className={cn(
                    'h-9 rounded-md text-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isToday && !isSelected && 'border border-primary/50 font-medium',
                    isSelected && 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                  )}
                >
                  {d}
                </button>
              )
            })}
          </div>

          <div className="mt-2 flex justify-between border-t pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
              disabled={!selected}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(toValue(today.y, today.m, today.d))
                setOpen(false)
              }}
            >
              Today
            </Button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  )
}
